import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ENVIRONMENT_SERVICE_TOKEN, ProductCategoryEnum } from '@mwe/constants';
import {
  HTTPError,
  HTTPResponse,
  IDefaultProdukt,
  INewClientConfirmationStatusResponse,
  IOlavEntry,
  IOrderDetailsInfo,
  IProduktAuswahl,
  IProduktAuswahlParams,
  IProduktVerfuegbarkeit,
  ISolrOlavKeyAddress,
  NewClientDetailsDTO,
} from '@mwe/models';
import { lastValueFrom, Observable } from 'rxjs';
import { EnvironmentService, NewClientStore, OrderService, StaticContentService } from '@mwe/services';

@Injectable({
  providedIn: 'root',
})
export class OrderNewClientService {
  private readonly http = inject(HttpClient);
  private readonly environmentService = inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN);
  private readonly orderService = inject(OrderService);
  private readonly store = inject(NewClientStore);
  private readonly staticContentService = inject(StaticContentService);
  private readonly config = signal<any>(undefined);
  private readonly configPromise: Promise<any>;

  constructor() {
    this.configPromise = Promise.resolve(this.staticContentService.loadProductsConfiguration()).then(staticProducts => {
      this.config.set(staticProducts);
      return staticProducts;
    });
  }

  getConfigPossibleCategories(): any {
    return this.config().possibleCategories;
  }

  getMultiProductCardCategories(): any {
    return this.config().multiProductCardCategories;
  }

  getAvailability(selectedOlav: IOlavEntry): Promise<IProduktVerfuegbarkeit[]> {
    return this.orderService.getAvailability(selectedOlav.identAdresse ? selectedOlav.identAdresse : selectedOlav);
  }

  getAlreadySelectedProductCategories(orderDetails: IOrderDetailsInfo[]): ProductCategoryEnum[] {
    return orderDetails.filter(elem => elem.status === 'Anmeldung').map(elem => ProductCategoryEnum[elem.category.toUpperCase()]);
  }

  getIpAddress(): Observable<{ remoteAddress: string }> {
    return this.http.get<{ remoteAddress: string }>('/api/public/newclient/ip-address');
  }

  async getProductsForSelection(params: IProduktAuswahlParams): Promise<HTTPResponse<IProduktAuswahl>> {
    return this.orderService.getProductsForSelection(params);
  }

  async getConfigDefaultTariffs(): Promise<IDefaultProdukt> {
    const config = this.config() ?? (await this.configPromise);
    const customerType = this.environmentService.getPortalCustomerType();
    return {
      strom: {
        tarifBeschreibung: config.tariffs.strom[customerType].name,
        tarifKey: config.tariffs.strom[customerType].shortcut,
        defaultOptions: config.tariffs.strom[customerType].defaultOptions,
      },
      gas: {
        tarifBeschreibung: config.tariffs.gas[customerType].name,
        tarifKey: config.tariffs.gas[customerType].shortcut,
        defaultOptions: config.tariffs.gas[customerType].defaultOptions,
      },
    };
  }

  async confirm(addressCode: string, dto: NewClientDetailsDTO): Promise<INewClientConfirmationStatusResponse> {
    try {
      const res = await lastValueFrom(
        this.http.post<INewClientConfirmationStatusResponse>(
          this.orderService.getResourceUrl('confirm') + '?addressCode=' + addressCode + '&newClientType=' + 0,
          dto,
        ),
      );
      this.store.updateStoreState({ submitId: res.submitId });
      return res;
    } catch (err) {
      throw new HTTPError(err.status, err.error, err.error?.message);
    }
  }

  async retry(submitId: number, dataDTO: NewClientDetailsDTO): Promise<INewClientConfirmationStatusResponse> {
    try {
      return await lastValueFrom(
        this.http.put<INewClientConfirmationStatusResponse>(
          this.orderService.getResourceUrl('retry') + '?submitId=' + submitId + '&newClientType=' + 0,
          dataDTO,
        ),
      );
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  //TODO: When old Address Input gets deleted refactor to Observable.
  async getAddressFromOlavKey(olavKey: string, processType?: string): Promise<ISolrOlavKeyAddress> {
    return this.orderService.getAddressFromOlavKey(olavKey, processType);
  }

  confirmStatus(submitId: number): Promise<INewClientConfirmationStatusResponse> {
    const url = this.orderService.getResourceUrl('confirmStatus') + '?submitId=' + submitId + '&newClientType=' + 0;
    try {
      return lastValueFrom(this.http.get<INewClientConfirmationStatusResponse>(url));
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }
}
